import Button from "../../../UiComponents/Button";
import { useEffect, useState } from "react";
import InputField from "../../../UiComponents/InputField";
import styled from "styled-components";
import { TextMuted } from "../../../UiComponents/text/StyledText";
import ScheduleDays from "./ScheduleDays";
import Checkbox from "../../../UiComponents/Checkbox";
import { createSchedule, updateSchedule } from "../../../../scripts/schedules";
import scheduleTypes from "./scheduleTypes";
import theme from "../../../../UI/theme";
import { FaExclamationTriangle, FaAngleDown, FaAngleUp, FaAngleDoubleDown, FaAngleDoubleUp } from "react-icons/fa";
import Radio from "../../../UiComponents/Radio";
import MultipleSelector from "../../../UiComponents/MultipleSelector";
import hierarchyTypes from "../device/hierarchyTypes";
import { ButtonRowContainer, ModalContainer, ModalHeader, ModalText } from "../../../panel/ModalStyles";
import { getAvailableSources } from "../../../installation/modules/sourceSelector/sourceSelectorUtils";
import { isHubVersionAtLeast } from "../../../installation/modules/hub/hubUtils";

const ColContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-self: start;
`

const RowContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
`

const RadioContainer = styled(RowContainer)`
    display: flex;
    flex-direction: row;

    :not(:last-of-type) {
        margin: 0 0 10px 0;
    }

    >div:not(:first-of-type) {
        margin-left: 10px;
    }
`

const ClickableText = styled(TextMuted)`
    display: block;
    cursor: pointer;
`

const ErrorMessage = styled.label`
    display: flex;
    align-items: center;
    color: ${theme.colors.raspberryRed};
`

const emptySchedule = {
    scheduleTypes: [],
    zones: [],
    days: [],
    isRegulating: false,
    vibeChange: 0,
    mute: false,
    moodSustain: false,
    sourceChanges: [],
    displayName: '',
    time: '12:00'
}

const scheduleTypeSelectorItems = [
    { key: scheduleTypes.types.VIBE, display: "Change vibe" },
    { key: scheduleTypes.types.REGULATION, display: "Turn automation on/off" },
    { key: scheduleTypes.types.MUTE, display: "Mute" },
    { key: scheduleTypes.types.MOOD_SUSTAIN, display: "Mood sustain" },
    { key: scheduleTypes.types.SOURCE, display: "Change source" }
];

const ScheduleForm = props => {
    const [time, setTime] = useState(toInputTime(props.schedule?.time) ?? '12:00');
    const [selectedSchedules, setSelectedSchedules] = useState(scheduleTypeSelectorItems.filter(item => props.schedule?.scheduleTypes?.some(type => type === item.key)) ?? []);
    const [errorMessage, setErrorMessage] = useState();
    const [selectedZoneSourceSelectors, setSelectedZoneSourceSelectors] = useState([]);
    const [availableSources, setAvailableSources] = useState({});
    const [schedule, setSchedule] = useState({
        ...emptySchedule,
        ...(props.schedule ?? {}),
        customerId: props.customerId,
        // extra hardening if API returns nulls:
        zones: props.schedule?.zones ?? [],
        days: props.schedule?.days ?? [],
        scheduleTypes: props.schedule?.scheduleTypes ?? [],
        sourceChanges: props.schedule?.sourceChanges ?? [],
    });

    // fire a useEffect any time the selcted zones change to update the source selector
    useEffect(() => {

        const zones = props.zones ?? [];
        const sourceSelectors = props.sourceSelectors ?? [];
        const sources = props.sources ?? [];

        if (!zones.length || !sourceSelectors.length || !sources.length) {
            setSelectedZoneSourceSelectors([]);
            setAvailableSources({});
            return;
        }

        setSelectedZoneSourceSelectors([]);
        setAvailableSources({});

        
        const selectedZoneIds = schedule.zones;
        
        if (selectedZoneIds?.length === 0 || !zones.some(zone => selectedZoneIds.includes(zone.zoneId) && zone.sourceSelectorId)) {
            setSelectedZoneSourceSelectors([]);
            setAvailableSources({});
            setSchedule(prev => {
                return { ...prev, sourceChanges: [] };
            });
            return;
        }
        
        // find relevant source selectors for the selected zones
        let relevantSourceSelectors = [];
        selectedZoneIds.forEach(zoneId => {
            const zone = zones.find(z => z.zoneId === zoneId);
            if (!zone?.sourceSelectorId) return;
            const sourceSelector = sourceSelectors.find(selector => selector.sourceSelectorId === zone.sourceSelectorId);
            if (sourceSelector && !relevantSourceSelectors.some(selector => selector.sourceSelectorId === sourceSelector.sourceSelectorId)) {
                relevantSourceSelectors.push(sourceSelector);
            }
        });
        setSelectedZoneSourceSelectors(relevantSourceSelectors);
        

        // find and sort relevant sources for the selected source selectors and place in the available sources state as an object with sourceSelectorId as the key
        let sourcesBySelectorId = {};
        relevantSourceSelectors.forEach(selector => {
            const sourcesForSelector = sources.filter(source => source.processorId === selector.processorId);
            const confirmedSources = getAvailableSources(selector, sourcesForSelector);
            sourcesBySelectorId[selector.sourceSelectorId] = confirmedSources ?? [];
        });
        setAvailableSources(sourcesBySelectorId);
        
    }, [schedule.zones, props.zones, props.sourceSelectors, props.sources]);

    const selectZoneHandler = (zone) => {
        setSchedule(prev => {
            var newZones = [...prev.zones];
            if (newZones.includes(zone)) {
                newZones = newZones.filter(z => z !== zone);
            } else {
                newZones.push(zone);
            }
            return { ...prev, zones: newZones };
        });
    }

    const changeScheduleTypeHandler = (newType) => {
        setSelectedSchedules(prev => {
            if (prev?.some(scheduleType => scheduleType.key === newType.key)) {
                return prev.filter(scheduleType => scheduleType.key !== newType.key);
            } else {
                var newScheduleTypes = [...prev];
                newScheduleTypes.push(newType);

                if (newType.key === scheduleTypes.types.VIBE) {
                    setSchedule({ ...schedule, vibeChange: 0 });
                }
                if (newType.key === scheduleTypes.types.REGULATION) {
                    setSchedule({ ...schedule, isRegulating: true });
                }
                if (newType.key === scheduleTypes.types.MUTE) {
                    setSchedule({ ...schedule, mute: true });
                }
                if (newType.key === scheduleTypes.types.MOOD_SUSTAIN) {
                    setSchedule({ ...schedule, moodSustain: true });
                }
                if (newType.key === scheduleTypes.types.SOURCE) {
                    setSchedule({ ...schedule, sourceChanges: [] });
                }   

                return newScheduleTypes;
            }
        })
    }

    const saveScheduleHandler = async () => {
        var newSchedule = {
            customerId: schedule.customerId,
            scheduleTypes: selectedSchedules.map(type => type.key),
            displayName: schedule.displayName,
            time: toScheduleTime(time),
            zones: [...schedule.zones],
            days: [...schedule.days],
        }

        if (schedule.scheduleId) {
            newSchedule.scheduleId = schedule.scheduleId;
        }

        if (selectedSchedules.some(type => type.key === scheduleTypes.types.VIBE)) {
            newSchedule.vibeChange = schedule.vibeChange;
        }
        if (selectedSchedules.some(type => type.key === scheduleTypes.types.REGULATION)) {
            newSchedule.isRegulating = +schedule.isRegulating;
        }
        if (selectedSchedules.some(type => type.key === scheduleTypes.types.MUTE)) {
            newSchedule.mute = +schedule.mute;
        }
        if (selectedSchedules.some(type => type.key === scheduleTypes.types.MOOD_SUSTAIN)) {
            newSchedule.moodSustain = +schedule.moodSustain;
        }
        if (selectedSchedules.some(type => type.key === scheduleTypes.types.SOURCE)) {
            newSchedule.sourceChanges = schedule.sourceChanges;
        }

        let res;
        try {
        res = newSchedule.scheduleId
            ? await updateSchedule(newSchedule)
            : await createSchedule(newSchedule);
        } catch (error) {
            setErrorMessage(error?.message ?? "Failed to save schedule");
        return;
        }

        if (res?.valid === undefined && res?.error === undefined) {
            props.setShowScheduleModal(false);
            await props.refreshSchedules();
        } else {
            setErrorMessage(res?.error ?? "Unknown error");
        }
    }


    return <ModalContainer>
        <ModalHeader>Schedule</ModalHeader>

        <ModalText>Schedule Name</ModalText>
        <InputField placeholder='Closing time' value={schedule.displayName} onChange={(e) => setSchedule({ ...schedule, displayName: e.target.value })} />

        <ModalText>Time</ModalText>
        <InputField type="time" value={time} onChange={(e) => setTime(e.target.value)} />

        <ModalText>Days</ModalText>
        <ScheduleDays editable days={schedule.days} onSelectDay={(newDays) => setSchedule({ ...schedule, days: newDays })} />

        <ModalText>Zones</ModalText>
        <div>
            {props.zones?.filter(zone => zone.hierarchyType !== hierarchyTypes.MASTER.value).map((zone, index) => {
                if (zone.hide) {
                    return;
                }
                return <RowContainer key={index}>
                    <Checkbox checked={schedule.zones?.includes(zone.zoneId)} onChange={() => { selectZoneHandler(zone.zoneId); }} />
                    <ClickableText onClick={() => { selectZoneHandler(zone.zoneId); }} >&nbsp;{zone.zoneName}</ClickableText>
                </RowContainer>
            })}
        </div>

        <div>
            <ModalText>Schedule actions</ModalText>
            <MultipleSelector editable items={scheduleTypeSelectorItems} sourceSelectors={props.sourceSelectors} selectedItems={selectedSchedules} onSelectItem={(selectedItem) => changeScheduleTypeHandler(selectedItem)} />
        </div>

        {selectedSchedules?.length > 0 ? <>
            <ColContainer>
                {selectedSchedules.some(type => type.key === scheduleTypes.types.VIBE) ?
                    <>
                        <TextMuted>Vibe</TextMuted>

                        <RadioContainer>
                            <Radio name="vibe" checked={schedule.vibeChange === -2} onChange={() => setSchedule({ ...schedule, vibeChange: -2 })} />
                            <TextMuted><FaAngleDoubleDown /></TextMuted>
                            <Radio name="vibe" checked={schedule.vibeChange === -1} onChange={() => setSchedule({ ...schedule, vibeChange: -1 })} />
                            <TextMuted><FaAngleDown /></TextMuted>
                            <Radio name="vibe" checked={schedule.vibeChange === 1} onChange={() => setSchedule({ ...schedule, vibeChange: 1 })} />
                            <TextMuted><FaAngleUp /></TextMuted>
                            <Radio name="vibe" checked={schedule.vibeChange === 2} onChange={() => setSchedule({ ...schedule, vibeChange: 2 })} />
                            <TextMuted><FaAngleDoubleUp /></TextMuted>
                            <Radio name="vibe" checked={schedule.vibeChange === 0} onChange={() => setSchedule({ ...schedule, vibeChange: 0 })} />
                            <TextMuted>Reset</TextMuted>
                        </RadioContainer>
                    </>
                    : <></>}

                {selectedSchedules.some(type => type.key === scheduleTypes.types.REGULATION) ?
                    <>
                        <TextMuted>Automation</TextMuted>

                        <RadioContainer>
                            <Radio name="regulation" checked={schedule.isRegulating} onChange={() => setSchedule({ ...schedule, isRegulating: true })} />
                            <TextMuted>On</TextMuted>
                            <Radio name="regulation" checked={!schedule.isRegulating} onChange={() => setSchedule({ ...schedule, isRegulating: false })} />
                            <TextMuted>Off</TextMuted>
                        </RadioContainer>
                    </>
                    : <></>}

                {selectedSchedules.some(type => type.key === scheduleTypes.types.MUTE) ?
                    <>
                        <TextMuted>Music</TextMuted>

                        <RadioContainer>
                            <Radio name="mute" checked={schedule.mute} onChange={() => setSchedule({ ...schedule, mute: true })} />
                            <TextMuted>Mute</TextMuted>
                            <Radio name="mute" checked={!schedule.mute} onChange={() => setSchedule({ ...schedule, mute: false })} />
                            <TextMuted>Unmute</TextMuted>
                        </RadioContainer>
                    </>
                    : <></>}

                {selectedSchedules.some(type => type.key === scheduleTypes.types.MOOD_SUSTAIN) ?
                    <>
                        <TextMuted>Mood sustain</TextMuted>

                        <RadioContainer>
                            <Radio name="moodsustain" checked={schedule.moodSustain} onChange={() => setSchedule({ ...schedule, moodSustain: true })} ></Radio>
                            <TextMuted>On</TextMuted>
                            <Radio name="moodsustain" checked={!schedule.moodSustain} onChange={() => setSchedule({ ...schedule, moodSustain: false })} ></Radio>
                            <TextMuted>Off</TextMuted>
                        </RadioContainer>
                    </> : <></>}

                {selectedSchedules.some(type => type.key === scheduleTypes.types.SOURCE) && <>

                    <ModalText style={{margin: '10px 0'}}>Source Changes</ModalText>

                    {!schedule.zones.length ? 
                        <TextMuted>Please select at least one zone</TextMuted>
                    : <>
                        {selectedZoneSourceSelectors.length > 0 ? <>
                        
                            {selectedZoneSourceSelectors.map((selector, index) => {

                                let zones = (props.zones ?? []).filter(
                                    zone => zone.sourceSelectorId === selector.sourceSelectorId && schedule.zones.includes(zone.zoneId)
                                );

                                if (zones.length === 0) {
                                    return null;
                                }
                                // the right hub by using zone.hubId and finding the hub in props.hubs
                                let zoneHub = (props.hubs ?? []).find(hub => hub.hubId === zones[0].hubId);
                                let hubHasSourceSchedules = isHubVersionAtLeast(zoneHub?.programVersion, '10.0.0');

                                if (!hubHasSourceSchedules) {
                                    return <ColContainer key={index} style={{ marginBottom: '10px'}}>
                                        <TextMuted style={{margin: '5px 0'}}>Ask Waved to update the hub to version 10.1.0 or later to use this feature in {zones.map(zone => zone.zoneName).join(', ')}.</TextMuted>
                                    </ColContainer>
                                }

                                return <ColContainer key={index} style={{ marginBottom: '10px'}}>
                                    <TextMuted style={{margin: '5px 0'}}>Select new source for: {zones.map(zone => zone.zoneName).join(', ')}</TextMuted>
                                    {(availableSources?.[selector.sourceSelectorId] ?? []).map((source, srcIndex) => {
                                        return <RowContainer key={srcIndex} style={{ marginBottom: '5px' }}>
                                            {schedule.sourceChanges?.some(change => change.sourceSelectorId === selector.sourceSelectorId && change.sourceId === source.sourceId) ? <>

                                                <Radio
                                                    name={`sourceSelector-${selector.sourceSelectorId}`}
                                                    checked={schedule.sourceChanges?.some(change => change.sourceSelectorId === selector.sourceSelectorId && change.sourceId === source.sourceId)}
                                                    onChange={() => {
                                                        // remove the source change for this selector
                                                        let newSourceChanges = [...schedule.sourceChanges];
                                                        newSourceChanges = newSourceChanges.filter(change => change.sourceSelectorId !== selector.sourceSelectorId);
                                                        setSchedule({ ...schedule, sourceChanges: newSourceChanges });
                                                    }}
                                                />
                                                <TextMuted>&nbsp;{source.name}</TextMuted>

                                            </>:<>
                                                <Radio
                                                    name={`sourceSelector-${selector.sourceSelectorId}`}
                                                    checked={schedule.sourceChanges?.some(change => change.sourceSelectorId === selector.sourceSelectorId && change.sourceId === source.sourceId)}
                                                    onChange={() => {
                                                        // add the source change for this selector
                                                        let newSourceChanges = [...schedule.sourceChanges];
                                                        // remove any existing change for this selector
                                                        newSourceChanges = newSourceChanges.filter(change => change.sourceSelectorId !== selector.sourceSelectorId);
                                                        newSourceChanges.push({ sourceSelectorId: selector.sourceSelectorId, sourceId: source.sourceId });
                                                        setSchedule({ ...schedule, sourceChanges: newSourceChanges });
                                                    }}
                                                />
                                                <TextMuted>&nbsp;{source.name}</TextMuted>
                                            </>}
                                        </RowContainer>
                                    })}
                                    
                                </ColContainer>
                            })}

                        </>: <TextMuted>No source selectors available for the selected zones</TextMuted>}
                    </>}


                </>}

            </ColContainer>
        </> : <></>}

        {errorMessage ? <ErrorMessage><FaExclamationTriangle />&nbsp;{errorMessage}</ErrorMessage> : <></>}

        <ButtonRowContainer>
            <Button primary onClick={() => saveScheduleHandler()}>Save</Button>
            <Button secondary onClick={() => props.setShowScheduleModal(false)}>Cancel</Button>
        </ButtonRowContainer>

    </ModalContainer>
}

export default ScheduleForm;

function toScheduleTime(timeInput) {
    if (timeInput && timeInput.length === 5) {
        return `${timeInput.substring(0, 2)}${timeInput.substring(3, 5)}`;
    } else {
        return null;
    }
}

function toInputTime(scheduleTime) {
    if (scheduleTime?.length === 4) {
        return `${scheduleTime.substring(0, 2)}:${scheduleTime.substring(2, 4)}`;
    } else {
        return undefined;
    }
}